import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  MenuItem,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  STATE_NUMBERS,
  generateUniqueId,
  formatFormId,
} from "../utils/formIdUtils";
import { lookupByType, matchRegistryType } from "../utils/formRegistry";

const PLACEHOLDER_FORM_ID = "00000000-0000-0000-0000-000000000000";

// Add no-select style
const noSelectStyle = {
  WebkitTouchCallout: "none",
  WebkitUserSelect: "none",
  KhtmlUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  userSelect: "none",
};

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

const PREDEFINED_CATEGORIES = [
  "salesContract",
  "clientRelationshipAgreements",
  "addendaToContract",
  "disclosures",
  "closing",
  "notices",
  "earnestMoney",
  "other",
];

// Function to load categories from localStorage
const loadSavedCategories = () => {
  const savedCategories = localStorage.getItem("customCategories");
  if (savedCategories) {
    try {
      const parsed = JSON.parse(savedCategories);
      return [...PREDEFINED_CATEGORIES, ...parsed];
    } catch (e) {
      console.error("Error loading saved categories:", e);
      return PREDEFINED_CATEGORIES;
    }
  }
  return PREDEFINED_CATEGORIES;
};

const FormDetailsSelector = ({
  formDetails,
  onFormDetailsChange,
  formRules = { formNotes: "", fields: [] },
  onFormRulesChange,
  variant = "default",
  extraFormSummary,
  onExtraFormSummaryChange,
  extraDealSummary,
  onExtraDealSummaryChange,
  signatures = {},
  registry = null,
  registryValidation = null,
}) => {
  const [openNewCategory, setOpenNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [categories, setCategories] = useState(loadSavedCategories);
  const [formIdInput, setFormIdInput] = useState("");
  const [suffixEditEnabled, setSuffixEditEnabled] = useState(false);
  const [customFormIdEnabled, setCustomFormIdEnabled] = useState(false);
  const [customFormIdValue, setCustomFormIdValue] = useState("");
  const [standardFormIdSnapshot, setStandardFormIdSnapshot] = useState("");
  /** What the registry made of the typed Form Type, set on blur. See handleTypeBlur. */
  const [typeHint, setTypeHint] = useState(null);

  const lastFourReadOnly = !suffixEditEnabled || customFormIdEnabled;

  const resolveStandardFormId = () => {
    const snapshot = standardFormIdSnapshot.trim();
    if (snapshot && snapshot !== PLACEHOLDER_FORM_ID) {
      return snapshot;
    }

    const locale = formDetails.locale;
    const type = (formDetails.type || "").trim();
    if (registry && locale && type) {
      const matches = lookupByType(registry, locale, type);
      if (matches.length === 1) {
        return matches[0].formId;
      }
    }

    if (locale) {
      const stateNumber = STATE_NUMBERS[locale] || "0000";
      return formatFormId(stateNumber);
    }

    return PLACEHOLDER_FORM_ID;
  };

  /**
   * Reconcile the typed Form Type with the registry when the field loses focus.
   *
   * The registry is the controlled vocabulary — types are never invented here, and a new one
   * only exists once it has gone through the bundle. So:
   *   - the same word in different case  → rewrite it to the registry's spelling
   *   - a prefix of one or more types    → name them, and let the author finish typing
   *   - neither                          → say it is not a registry type, change nothing
   *
   * Suggestions are deliberately text, not a picker: a click-to-fill list invites taking
   * whatever is nearest rather than the type the form actually is.
   */
  const handleTypeBlur = () => {
    const typed = (formDetails.type || "").trim();
    if (!registry || !typed) {
      setTypeHint(null);
      return;
    }

    const { exact, suggestions } = matchRegistryType(registry, typed);

    if (exact) {
      if (exact !== typed) onFormDetailsChange({ ...formDetails, type: exact });
      setTypeHint(null);
      return;
    }
    if (suggestions.length) {
      setTypeHint(`Not a registry type. Did you mean: ${suggestions.join(", ")}?`);
      return;
    }
    setTypeHint(`"${typed}" is not a type in the form registry.`);
  };

  const toggleSettingsTypeRadio = (field, optionKey) => {
    const current = formDetails.settings?.type?.[field] || "";
    const next = current === optionKey ? "" : optionKey;
    onFormDetailsChange({
      ...formDetails,
      settings: {
        ...formDetails.settings,
        type: {
          ...formDetails.settings?.type,
          [field]: next,
        },
      },
    });
  };

  // Rule flags are additive: they publish into settings.type next to the radio picks rather
  // than replacing them, so they're held as a boolean map under settings.type.rules and each
  // one toggles independently. Cleared to undefined rather than false so an unset rule leaves
  // no key behind in the published JSON.
  const toggleSettingsTypeRule = (ruleKey, checked) => {
    onFormDetailsChange({
      ...formDetails,
      settings: {
        ...formDetails.settings,
        type: {
          ...formDetails.settings?.type,
          rules: {
            ...formDetails.settings?.type?.rules,
            [ruleKey]: checked || undefined,
          },
        },
      },
    });
  };

  const toggleSettingsReq = (reqKey, checked) => {
    onFormDetailsChange({
      ...formDetails,
      settings: {
        ...formDetails.settings,
        reqs: {
          ...formDetails.settings?.reqs,
          [reqKey]: checked || undefined,
        },
      },
    });
  };

  useEffect(() => {
    // Update form ID when locale changes (keep this for backend reference)
    // Only generate new formId if one doesn't exist or if it's the default placeholder
    // IMPORTANT: This preserves imported formIds that are not the default placeholder
    if (formDetails.locale && !customFormIdEnabled) {
      const stateNumber = STATE_NUMBERS[formDetails.locale] || "0000";
      const expectedFormId = formatFormId(stateNumber);

      // Check if current formId is the default placeholder
      const isDefaultFormId =
        !formDetails.formId ||
        formDetails.formId === "00000000-0000-0000-0000-000000000000";

      // Only auto-generate if formId is missing or is the default placeholder
      // This preserves imported formIds (e.g., "00000000-0000-0000-0000-0000000000009")
      if (isDefaultFormId) {
        onFormDetailsChange({
          ...formDetails,
          formId: expectedFormId,
        });
        setFormIdInput(expectedFormId.slice(-4));
      }
      // If formId exists and is not default, preserve it (don't overwrite imported values)
      // The separate useEffect below will handle updating formIdInput display
    }
  }, [formDetails.locale, customFormIdEnabled]);

  // Initialize formIdInput when formDetails.formId changes
  useEffect(() => {
    if (formDetails.formId) {
      setFormIdInput(formDetails.formId.slice(-4));
    }
  }, [formDetails.formId]);

  // Function to save custom categories
  const saveCategories = (updatedCategories) => {
    const customCategories = updatedCategories.filter(
      (cat) => !PREDEFINED_CATEGORIES.includes(cat),
    );
    localStorage.setItem("customCategories", JSON.stringify(customCategories));
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    if (field === "category" && value === "new") {
      setOpenNewCategory(true);
      return; // Don't update formDetails yet
    }
    onFormDetailsChange({
      ...formDetails,
      [field]: value,
    });
  };

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      const categoryValue = newCategory.trim();
      setCategories((prev) => {
        const updated = [...prev, categoryValue];
        saveCategories(updated);
        return updated;
      });
      onFormDetailsChange({
        ...formDetails,
        category: categoryValue,
      });
      setNewCategory("");
      setOpenNewCategory(false);
    }
  };

  const handleSuffixEditToggle = (enabled) => {
    if (enabled && customFormIdEnabled) {
      setCustomFormIdEnabled(false);
      setCustomFormIdValue("");
    }
    setSuffixEditEnabled(enabled);
  };

  const handleCustomFormIdToggle = (enabled) => {
    if (!enabled) {
      const restoredFormId = resolveStandardFormId();
      const customChanged =
        customFormIdValue.trim() &&
        customFormIdValue.trim() !== restoredFormId;
      if (customChanged) {
        const ok = window.confirm(
          "Turn off Custom Form ID and restore the standard form ID from before custom editing?",
        );
        if (!ok) return;
      }
    }
    if (enabled) {
      setSuffixEditEnabled(false);
    }
    setCustomFormIdEnabled(enabled);

    if (!enabled) {
      const restoredFormId = resolveStandardFormId();
      onFormDetailsChange({
        ...formDetails,
        formId: restoredFormId,
      });
      setFormIdInput(restoredFormId.slice(-4));
      setCustomFormIdValue("");
    } else {
      const currentFormId = formDetails.formId || PLACEHOLDER_FORM_ID;
      setStandardFormIdSnapshot(currentFormId);
      setCustomFormIdValue(currentFormId);
    }
  };

  const handleDeleteCategory = (categoryToDelete) => {
    // Update categories list
    setCategories((prev) => {
      const updated = prev.filter((cat) => cat !== categoryToDelete);
      saveCategories(updated);
      return updated;
    });

    // If the deleted category was selected, reset the form details category
    if (formDetails.category === categoryToDelete) {
      onFormDetailsChange({
        ...formDetails,
        category: "",
      });
    }
  };

  // Helper to check if a value is empty
  const isEmpty = (val) => val === undefined || val === null || val === "";

  const isAnnotatorVariant = variant === "annotator";

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Form Settings"
        subheader="Configure form metadata"
        sx={noSelectStyle}
      />
      <Divider />
      <CardContent
        sx={{
          ...noSelectStyle,
          px: 3, // Add horizontal padding
          pb: 3, // Add bottom padding
        }}
      >
        <Stack spacing={3}>
          <Grid
            container
            spacing={3}
            sx={{
              width: "100%",
              mx: "auto", // Center the grid
            }}
          >
            {!isAnnotatorVariant && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  required
                  error={isEmpty(formDetails.locale)}
                  label="State/Locale"
                  value={formDetails.locale || ""}
                  onChange={handleChange("locale")}
                  helperText="Select the state for this form"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {US_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {!isAnnotatorVariant && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  error={isEmpty(formDetails.type)}
                  label="Form Type"
                  value={formDetails.type || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Convert to camelCase while preserving user's ability to type
                    const camelCaseValue = value
                      .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters except spaces
                      .replace(/\s+([a-zA-Z0-9])/g, (match, group) =>
                        group.toUpperCase(),
                      ) // Convert spaces to camelCase
                      .replace(/^([A-Z])/, (match, group) =>
                        group.toLowerCase(),
                      ); // Ensure first character is lowercase

                    if (typeHint) setTypeHint(null);
                    onFormDetailsChange({
                      ...formDetails,
                      type: camelCaseValue,
                    });
                  }}
                  onBlur={handleTypeBlur}
                  helperText={
                    typeHint ||
                    "Must be an existing form-registry type. Spaces become camelCase; the case is corrected on exit."
                  }
                />
              </Grid>
            )}
            {!isAnnotatorVariant && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  error={isEmpty(formDetails.name)}
                  label="Form Name"
                  value={formDetails.name || ""}
                  onChange={handleChange("name")}
                  helperText="Enter the full name of the form"
                />
              </Grid>
            )}
            {!isAnnotatorVariant && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  error={isEmpty(formDetails.shortName)}
                  label="Short Name"
                  value={formDetails.shortName || ""}
                  onChange={handleChange("shortName")}
                  helperText="Enter a short name for the form"
                />
              </Grid>
            )}
            {!isAnnotatorVariant && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                    <TextField
                      fullWidth
                      label="Version"
                      value={formDetails.version ?? ""}
                      onChange={(e) =>
                        onFormDetailsChange({
                          ...formDetails,
                          version: e.target.value,
                        })
                      }
                      helperText="e.g. 21.2.3 — single source of truth for Save/Publish"
                    />
                  </Box>
                  {/* PDF V2 on its own line, the two view-mode flags beneath it —
                      they only apply to a PDF V2 form, so the nesting reads as such. */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <FormControlLabel
                      sx={{ mr: 0, whiteSpace: "nowrap" }}
                      control={
                        <Checkbox
                          checked={formDetails.settings?.pdfV2 !== false}
                          onChange={(e) =>
                            onFormDetailsChange({
                              ...formDetails,
                              settings: {
                                ...formDetails.settings,
                                pdfV2: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label="PDF V2"
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FormControlLabel
                        sx={{ mr: 0, whiteSpace: "nowrap" }}
                        control={
                          <Checkbox
                            checked={formDetails.settings?.sigOnly === true}
                            disabled={formDetails.settings?.shareOnly === true}
                            onChange={(e) =>
                              onFormDetailsChange({
                                ...formDetails,
                                settings: {
                                  ...formDetails.settings,
                                  sigOnly: e.target.checked,
                                },
                              })
                            }
                          />
                        }
                        label="Sig only"
                      />
                      <FormControlLabel
                        sx={{ mr: 0, whiteSpace: "nowrap" }}
                        control={
                          <Checkbox
                            checked={formDetails.settings?.shareOnly === true}
                            disabled={formDetails.settings?.sigOnly === true}
                            onChange={(e) =>
                              onFormDetailsChange({
                                ...formDetails,
                                settings: {
                                  ...formDetails.settings,
                                  shareOnly: e.target.checked,
                                },
                              })
                            }
                          />
                        }
                        label="Share only"
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Form Rules Notes"
                value={formRules.formNotes || ""}
                onChange={(e) =>
                  onFormRulesChange &&
                  onFormRulesChange((prev) => ({ ...prev, formNotes: e.target.value }))
                }
                helperText="Exported in form-rules.json"
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={isAnnotatorVariant ? 12 : 6}>
              <TextField
                select
                fullWidth
                required
                error={isEmpty(formDetails.category)}
                label="Category"
                value={formDetails.category || ""}
                onChange={handleChange("category")}
                helperText="Select or add a new category"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem
                    key={category}
                    value={category}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>{category}</span>
                    {!PREDEFINED_CATEGORIES.includes(category) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteCategory(category);
                        }}
                        sx={{
                          ml: 1,
                          opacity: 0.7,
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </MenuItem>
                ))}
                <MenuItem
                  value="new"
                  sx={{
                    borderTop: 1,
                    borderColor: "divider",
                    mt: 1,
                    color: "primary.main",
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} /> Add New Category
                </MenuItem>
              </TextField>
            </Grid>
            {!isAnnotatorVariant && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  error={isEmpty(formIdInput)}
                  label="Form ID (Last 4 digits)"
                  value={formIdInput}
                  onChange={(e) => {
                    if (lastFourReadOnly) return;
                    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setFormIdInput(value);
                  }}
                  onBlur={(e) => {
                    if (lastFourReadOnly) return;
                    const lastFourDigits = e.target.value.padStart(4, "0");
                    const currentFormId =
                      formDetails.formId ||
                      "00000000-0000-0000-0000-000000000000";
                    const newFormId =
                      currentFormId.slice(0, -4) + lastFourDigits;
                    onFormDetailsChange({
                      ...formDetails,
                      formId: newFormId,
                    });
                    setFormIdInput(lastFourDigits);
                  }}
                  InputProps={{
                    readOnly: lastFourReadOnly,
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                    },
                    ...(lastFourReadOnly && {
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "action.hover",
                      },
                    }),
                  }}
                  inputProps={{
                    maxLength: 4,
                    pattern: "[0-9]*",
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5, px: "12px" }}>
                  <Typography variant="caption" color="text.secondary">
                    {suffixEditEnabled && !customFormIdEnabled
                      ? "Edit the last 4 digits of the form ID"
                      : "Read-only"}
                  </Typography>
                  <Tooltip title="Edit suffix" arrow placement="top">
                    <Checkbox
                      size="small"
                      checked={suffixEditEnabled && !customFormIdEnabled}
                      disabled={customFormIdEnabled}
                      onChange={(e) => {
                        handleSuffixEditToggle(e.target.checked);
                      }}
                      sx={{
                        p: 0,
                        "& .MuiSvgIcon-root": { fontSize: "1rem" },
                      }}
                    />
                  </Tooltip>
                </Box>
              </Grid>
            )}
            {!isAnnotatorVariant && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  error={isEmpty(formDetails.metadata?.order)}
                  label="Category Order"
                  value={formDetails.metadata?.order ?? ""}
                  onChange={(e) => {
                    // Only allow integer inputs. Stored as a number — the
                    // published JSON must carry `"order": 20`, not `"20"`.
                    const digits = e.target.value.replace(/[^0-9]/g, "");
                    onFormDetailsChange({
                      ...formDetails,
                      metadata: {
                        ...formDetails.metadata,
                        order: digits === "" ? null : Number(digits),
                      },
                    });
                  }}
                  helperText="Category Order (required)"
                />
              </Grid>
            )}
            {!isAnnotatorVariant && (
              <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Form ID"
                    value={
                      customFormIdEnabled
                        ? customFormIdValue
                        : formDetails.formId ||
                          "00000000-0000-0000-0000-000000000000"
                    }
                    onChange={(e) => {
                      if (customFormIdEnabled) {
                        // Only allow numbers and hyphens
                        const filteredValue = e.target.value.replace(
                          /[^0-9-]/g,
                          "",
                        );
                        setCustomFormIdValue(filteredValue);
                        onFormDetailsChange({
                          ...formDetails,
                          formId: filteredValue,
                        });
                      }
                    }}
                    InputProps={{
                      readOnly: !customFormIdEnabled,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      },
                      ...(!customFormIdEnabled && {
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "action.hover",
                        },
                      }),
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5, px: "12px" }}>
                    <Typography variant="caption" color="text.secondary">
                      {customFormIdEnabled ? "Enter custom form ID" : "Read-only"}
                    </Typography>
                    <Tooltip title="Custom Id" arrow placement="top">
                      <Checkbox
                        size="small"
                        checked={customFormIdEnabled}
                        onChange={(e) => {
                          handleCustomFormIdToggle(e.target.checked);
                        }}
                        sx={{
                          p: 0,
                          "& .MuiSvgIcon-root": { fontSize: "1rem" },
                        }}
                      />
                    </Tooltip>
                  </Box>
              </Grid>
            )}
            {!isAnnotatorVariant && registryValidation && !registryValidation.skipped && (
              <Grid item xs={12}>
                {registryValidation.valid ? (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    {registryValidation.message ||
                      (registryValidation.registryRow
                        ? `Registry (${formDetails.locale}): ${registryValidation.registryRow.type}`
                        : "Registry alignment OK")}
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ py: 0.5 }}>
                    {registryValidation.message || "Form ID and type do not match the registry."}
                  </Alert>
                )}
                {registry?.exportedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, px: "12px" }}>
                    Registry exported: {registry.exportedAt}
                  </Typography>
                )}
              </Grid>
            )}
            {!isAnnotatorVariant && formDetails.locale && registry && !registry?.locales?.[formDetails.locale] && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  No registry entries for locale &quot;{formDetails.locale}&quot;.
                </Alert>
              </Grid>
            )}
          </Grid>

          {/* Data Extraction Metadata Section */}
          {!isAnnotatorVariant && (
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="data-extraction-content"
                id="data-extraction-header"
              >
                <Typography>Data Extraction Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Document Type"
                      value={formDetails.dataExtraction?.sensible?.documentType || ""}
                      onChange={(e) => {
                        onFormDetailsChange({
                          ...formDetails,
                          dataExtraction: {
                            ...formDetails.dataExtraction,
                            sensible: { ...formDetails.dataExtraction?.sensible, documentType: e.target.value },
                          },
                        });
                      }}
                      helperText="e.g., colorado_contract"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Config Name"
                      value={formDetails.dataExtraction?.sensible?.configName || ""}
                      onChange={(e) => {
                        onFormDetailsChange({
                          ...formDetails,
                          dataExtraction: {
                            ...formDetails.dataExtraction,
                            sensible: { ...formDetails.dataExtraction?.sensible, configName: e.target.value },
                          },
                        });
                      }}
                      helperText="e.g., ctbs"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Config Version"
                      value={formDetails.dataExtraction?.sensible?.configVersion || ""}
                      onChange={(e) => {
                        onFormDetailsChange({
                          ...formDetails,
                          dataExtraction: {
                            ...formDetails.dataExtraction,
                            sensible: { ...formDetails.dataExtraction?.sensible, configVersion: e.target.value },
                          },
                        });
                      }}
                      helperText="Configuration version hash"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Demo Files</Typography>
                    {(formDetails.dataExtraction?.sensible?.demoFiles || []).map((demoFile, idx) => (
                      <Grid container spacing={2} key={idx} sx={{ mb: 1 }}>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Demo Filename"
                            value={demoFile.demoFilename || ""}
                            onChange={(e) => {
                              const demoFiles = [...(formDetails.dataExtraction?.sensible?.demoFiles || [])];
                              demoFiles[idx] = { ...demoFiles[idx], demoFilename: e.target.value };
                              onFormDetailsChange({
                                ...formDetails,
                                dataExtraction: {
                                  ...formDetails.dataExtraction,
                                  sensible: { ...formDetails.dataExtraction?.sensible, demoFiles },
                                },
                              });
                            }}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Demo Extraction ID"
                            value={demoFile.demoExtractionId || ""}
                            onChange={(e) => {
                              const demoFiles = [...(formDetails.dataExtraction?.sensible?.demoFiles || [])];
                              demoFiles[idx] = { ...demoFiles[idx], demoExtractionId: e.target.value };
                              onFormDetailsChange({
                                ...formDetails,
                                dataExtraction: {
                                  ...formDetails.dataExtraction,
                                  sensible: { ...formDetails.dataExtraction?.sensible, demoFiles },
                                },
                              });
                            }}
                            sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.875rem" } }}
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const demoFiles = (formDetails.dataExtraction?.sensible?.demoFiles || []).filter((_, i) => i !== idx);
                              onFormDetailsChange({
                                ...formDetails,
                                dataExtraction: {
                                  ...formDetails.dataExtraction,
                                  sensible: { ...formDetails.dataExtraction?.sensible, demoFiles },
                                },
                              });
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const demoFiles = [...(formDetails.dataExtraction?.sensible?.demoFiles || []), { demoFilename: "", demoExtractionId: "" }];
                        onFormDetailsChange({
                          ...formDetails,
                          dataExtraction: {
                            ...formDetails.dataExtraction,
                            sensible: { ...formDetails.dataExtraction?.sensible, demoFiles },
                          },
                        });
                      }}
                    >
                      Add Demo File
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Form Settings Accordion */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="form-settings-content"
              id="form-settings-header"
            >
              <Typography>Form Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formDetails.otherSideCanUpload !== false}
                        onChange={(e) => {
                          onFormDetailsChange({
                            ...formDetails,
                            otherSideCanUpload: e.target.checked,
                          });
                        }}
                      />
                    }
                    label="Other side can upload"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Type
                  </Typography>
                  <RadioGroup value={formDetails.settings?.type?.type || ""}>
                    {[
                      {
                        key: "isPurchaseContract",
                        label: "Is Purchase Contract",
                      },
                      {
                        key: "isPurchaseContractCounter",
                        label: "Is Purchase Contract Counter",
                      },
                      {
                        key: "isPurchaseContractAmend",
                        label: "Is Purchase Contract Amend",
                      },
                      {
                        key: "isPurchaseContractAddendum",
                        label: "Is Purchase Contract Addendum",
                      },
                      { key: "isChangeOfStatus", label: "Is Change Of Status" },
                      // Keep in step with TYPE_KEYS in utils/formParser.js — a key the parser
                      // does not know is dropped from the published JSON.
                      {
                        key: "isClientListingContract",
                        label: "Is Client Listing Contract",
                      },
                      {
                        key: "isClientListingContractAmend",
                        label: "Is Client Listing Contract Amend",
                      },
                      { key: "isCustomAddendum", label: "Is Custom Addendum" },
                      {
                        key: "isPurchaseContractAgreementToRevive",
                        label: "Is Purchase Contract Agreement To Revive",
                      },
                      {
                        key: "isExtensionOrTerm_2SC",
                        label: "Is Extension Or Term (2 Signers)",
                      },
                      {
                        key: "isInspectionObjectionNotice",
                        label: "Is Inspection Objection Notice",
                      },
                      {
                        key: "isInspectionObjectionNoticeWithdrawal",
                        label: "Is Inspection Objection Notice Withdrawal",
                      },
                      {
                        key: "isInspectionResolution",
                        label: "Is Inspection Resolution",
                      },
                      {
                        key: "isAppraisalObjectionNotice",
                        label: "Is Appraisal Objection Notice",
                      },
                      {
                        key: "isAppraisalObjectionNoticeWithdrawal",
                        label: "Is Appraisal Objection Notice Withdrawal",
                      },
                      {
                        key: "isTitleRelatedObjectionNotice",
                        label: "Is Title Related Objection Notice",
                      },
                      {
                        key: "isTitleRelatedObjectionNoticeWithdrawal",
                        label: "Is Title Related Objection Notice Withdrawal",
                      },
                      { key: "isSellerNetSheet", label: "Is Seller Net Sheet" },
                    ].map((option) => (
                      <FormControlLabel
                        key={option.key}
                        value={option.key}
                        control={
                          <Radio
                            checked={
                              (formDetails.settings?.type?.type || "") ===
                              option.key
                            }
                            onClick={() =>
                              toggleSettingsTypeRadio("type", option.key)
                            }
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Property Type
                  </Typography>
                  <RadioGroup
                    value={formDetails.settings?.type?.propertyType || ""}
                  >
                    {[
                      { key: "isResidential", label: "Is Residential" },
                      { key: "isCommercial", label: "Is Commercial" },
                      { key: "isLand", label: "Is Land" },
                      { key: "isManufactured", label: "Is Manufactured" },
                      { key: "isComplianceForm", label: "Is Compliance Form" },
                      { key: "isBuilder", label: "Is Builder" },
                      { key: "isNewConstruction", label: "Is New Construction" },
                    ].map((option) => (
                      <FormControlLabel
                        key={option.key}
                        value={option.key}
                        control={
                          <Radio
                            checked={
                              (formDetails.settings?.type?.propertyType ||
                                "") === option.key
                            }
                            onClick={() =>
                              toggleSettingsTypeRadio("propertyType", option.key)
                            }
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Rules
                  </Typography>
                  <Stack>
                    {[
                      {
                        key: "actionExecutedTerminatesDeal",
                        label: "Action Executed Terminates Deal",
                      },
                      {
                        key: "allowsMultipleSignedInstances",
                        label: "Allows Multiple Signed Instances",
                      },
                    ].map((option) => (
                      <FormControlLabel
                        key={option.key}
                        control={
                          <Checkbox
                            checked={!!formDetails.settings?.type?.rules?.[option.key]}
                            onChange={(e) =>
                              toggleSettingsTypeRule(option.key, e.target.checked)
                            }
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Requirements
                  </Typography>
                  {/* Each reqs flag adds one precondition on the deal at creation time; they
                      compose, so every one is an independent checkbox. Unlike the Type radios
                      these are not allowlisted anywhere — settings.reqs passes through the
                      parser untouched — so this list is presentation only and a flag missing
                      from it survives a round-trip, it just can't be edited here. */}
                  <Stack>
                    {[
                      {
                        key: "requiresDealStatusUnderContract",
                        label: "Requires Deal Status Under Contract",
                      },
                      {
                        key: "requiresDealStatusTerminated",
                        label: "Requires Deal Status Terminated",
                      },
                      {
                        key: "requiresExecutedClientListingContract",
                        label: "Requires Executed Client Listing Contract",
                      },
                      {
                        key: "requiresLockedPurchaseContractSignedByBuyer",
                        label: "Requires Locked Purchase Contract Signed By Buyer",
                      },
                      {
                        key: "requiresLockedExtensionOrTermTarget",
                        label: "Requires Locked Extension Or Term Target",
                      },
                      {
                        key: "requiresLockedExtensionOrTermTargetSameSide",
                        label: "Requires Locked Extension Or Term Target (Same Side)",
                      },
                      {
                        key: "requiresExecutedInspectionObjectionNotice",
                        label: "Requires Executed Inspection Objection Notice",
                      },
                      {
                        key: "requiresExecutedAppraisalObjectionNotice",
                        label: "Requires Executed Appraisal Objection Notice",
                      },
                      {
                        key: "requiresExecutedTitleRelatedObjectionNotice",
                        label: "Requires Executed Title Related Objection Notice",
                      },
                      {
                        key: "blockedIfLockedExtensionOrTermTarget",
                        label: "Blocked If Locked Extension Or Term Target",
                      },
                      {
                        key: "blockedIfExecutedInspectionResolution",
                        label: "Blocked If Executed Inspection Resolution",
                      },
                      {
                        key: "blockedIfExecutedInspectionObjectionNoticeWithdrawal",
                        label: "Blocked If Executed Inspection Objection Notice Withdrawal",
                      },
                      {
                        key: "hiddenIfSelfExecuted",
                        label: "Hidden If Self Executed",
                      },
                      // Target marker, not a gate: it doesn't restrict THIS form, it tags it
                      // so the requiresLockedExtensionOrTermTarget* / blockedIfLocked… gates on
                      // other forms can find it. Lives in reqs even though it reads like a type.
                      {
                        key: "isExtensionOrTermTarget",
                        label: "Is Extension Or Term Target (marker)",
                      },
                    ].map((option) => (
                      <FormControlLabel
                        key={option.key}
                        control={
                          <Checkbox
                            checked={!!formDetails.settings?.reqs?.[option.key]}
                            onChange={(e) =>
                              toggleSettingsReq(option.key, e.target.checked)
                            }
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </Stack>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Days After
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formDetails.settings?.daysAfter?.mec}
                            onChange={(e) => {
                              onFormDetailsChange({
                                ...formDetails,
                                settings: {
                                  ...formDetails.settings,
                                  daysAfter: {
                                    ...formDetails.settings?.daysAfter,
                                    mec: e.target.checked || undefined,
                                  },
                                },
                              });
                            }}
                          />
                        }
                        label="MEC"
                      />
                      <TextField
                        size="small"
                        placeholder="after"
                        label="Preposition"
                        value={formDetails.settings?.daysAfter?.mecPreposition ?? "after"}
                        onChange={(e) => {
                          onFormDetailsChange({
                            ...formDetails,
                            settings: {
                              ...formDetails.settings,
                              daysAfter: {
                                ...formDetails.settings?.daysAfter,
                                mecPreposition: e.target.value || "after",
                              },
                            },
                          });
                        }}
                        sx={{ width: 120 }}
                        inputProps={{ "aria-label": "MEC preposition" }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formDetails.settings?.daysAfter?.bs}
                            onChange={(e) => {
                              onFormDetailsChange({
                                ...formDetails,
                                settings: {
                                  ...formDetails.settings,
                                  daysAfter: {
                                    ...formDetails.settings?.daysAfter,
                                    bs: e.target.checked || undefined,
                                  },
                                },
                              });
                            }}
                          />
                        }
                        label="ABS"
                      />
                      <TextField
                        size="small"
                        placeholder="after"
                        label="Preposition"
                        value={formDetails.settings?.daysAfter?.bsPreposition ?? "after"}
                        onChange={(e) => {
                          onFormDetailsChange({
                            ...formDetails,
                            settings: {
                              ...formDetails.settings,
                              daysAfter: {
                                ...formDetails.settings?.daysAfter,
                                bsPreposition: e.target.value || "after",
                              },
                            },
                          });
                        }}
                        sx={{ width: 120 }}
                        inputProps={{ "aria-label": "BS preposition" }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formDetails.settings?.daysAfter?.bad}
                            onChange={(e) => {
                              onFormDetailsChange({
                                ...formDetails,
                                settings: {
                                  ...formDetails.settings,
                                  daysAfter: {
                                    ...formDetails.settings?.daysAfter,
                                    bad: e.target.checked || undefined,
                                  },
                                },
                              });
                            }}
                          />
                        }
                        label="BAD"
                      />
                      <TextField
                        size="small"
                        placeholder="after"
                        label="Preposition"
                        value={formDetails.settings?.daysAfter?.badPreposition ?? "after"}
                        onChange={(e) => {
                          onFormDetailsChange({
                            ...formDetails,
                            settings: {
                              ...formDetails.settings,
                              daysAfter: {
                                ...formDetails.settings?.daysAfter,
                                badPreposition: e.target.value || "after",
                              },
                            },
                          });
                        }}
                        sx={{ width: 120 }}
                        inputProps={{ "aria-label": "BAD preposition" }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formDetails.settings?.daysAfter?.coe}
                            onChange={(e) => {
                              onFormDetailsChange({
                                ...formDetails,
                                settings: {
                                  ...formDetails.settings,
                                  daysAfter: {
                                    ...formDetails.settings?.daysAfter,
                                    coe: e.target.checked || undefined,
                                  },
                                },
                              });
                            }}
                          />
                        }
                        label="COE"
                      />
                      <TextField
                        size="small"
                        placeholder="after"
                        label="Preposition"
                        value={formDetails.settings?.daysAfter?.coePreposition ?? "after"}
                        onChange={(e) => {
                          onFormDetailsChange({
                            ...formDetails,
                            settings: {
                              ...formDetails.settings,
                              daysAfter: {
                                ...formDetails.settings?.daysAfter,
                                coePreposition: e.target.value || "after",
                              },
                            },
                          });
                        }}
                        sx={{ width: 120 }}
                        inputProps={{ "aria-label": "COE preposition" }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formDetails.settings?.daysAfter?.dbc}
                            onChange={(e) => {
                              onFormDetailsChange({
                                ...formDetails,
                                settings: {
                                  ...formDetails.settings,
                                  daysAfter: {
                                    ...formDetails.settings?.daysAfter,
                                    dbc: e.target.checked || undefined,
                                  },
                                },
                              });
                            }}
                          />
                        }
                        label="DBC"
                      />
                      <TextField
                        size="small"
                        placeholder="before"
                        label="Preposition"
                        value={formDetails.settings?.daysAfter?.dbcPreposition ?? "before"}
                        onChange={(e) => {
                          onFormDetailsChange({
                            ...formDetails,
                            settings: {
                              ...formDetails.settings,
                              daysAfter: {
                                ...formDetails.settings?.daysAfter,
                                dbcPreposition: e.target.value || "before",
                              },
                            },
                          });
                        }}
                        sx={{ width: 120 }}
                        inputProps={{ "aria-label": "DBC preposition" }}
                      />
                    </Box>
                  </Stack>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Form JSON Settings
                  </Typography>
                  <Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formDetails.lineNumbers}
                          onChange={(e) => {
                            onFormDetailsChange({
                              ...formDetails,
                              lineNumbers: e.target.checked,
                            });
                          }}
                        />
                      }
                      label="Enable Line Numbers"
                    />
                  </Stack>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Advanced: extra summary entries and rules contract order */}
          {!isAnnotatorVariant && onExtraFormSummaryChange && onExtraDealSummaryChange && (
            <Accordion elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Advanced — Summary overrides &amp; contract order</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Extra Form Summary</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Entries for attributeNames not in any field (formSummaryJSON).</Typography>
                    {(Object.entries(extraFormSummary || {})).map(([attr, entry]) => (
                      <Box key={attr} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                        <TextField size="small" label="Attribute" value={attr} sx={{ minWidth: 140 }} onChange={(e) => { const next = { ...(extraFormSummary || {}) }; delete next[attr]; next[e.target.value || attr] = entry; onExtraFormSummaryChange(next); }} />
                        <TextField size="small" label="Label" value={entry?.label ?? ""} sx={{ minWidth: 120 }} onChange={(e) => onExtraFormSummaryChange({ ...(extraFormSummary || {}), [attr]: { ...entry, label: e.target.value } })} />
                        <TextField size="small" label="Type" value={entry?.type ?? ""} sx={{ minWidth: 100 }} onChange={(e) => onExtraFormSummaryChange({ ...(extraFormSummary || {}), [attr]: { ...entry, type: e.target.value } })} />
                        <IconButton size="small" onClick={() => { const next = { ...(extraFormSummary || {}) }; delete next[attr]; onExtraFormSummaryChange(next); }} aria-label="Remove"><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Box>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={() => { const key = "extraFormSummary" + (Object.keys(extraFormSummary || {}).length + 1); onExtraFormSummaryChange({ ...(extraFormSummary || {}), [key]: { label: "", type: "input" } }); }}>Add entry</Button>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Extra Deal Summary</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Entries for attributeNames not in any field (dealSummaryJSON).</Typography>
                    {(Object.entries(extraDealSummary || {})).map(([attr, entry]) => (
                      <Box key={attr} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                        <TextField size="small" label="Attribute" value={attr} sx={{ minWidth: 140 }} onChange={(e) => { const next = { ...(extraDealSummary || {}) }; delete next[attr]; next[e.target.value || attr] = entry; onExtraDealSummaryChange(next); }} />
                        <TextField size="small" label="Label" value={entry?.label ?? ""} sx={{ minWidth: 120 }} onChange={(e) => onExtraDealSummaryChange({ ...(extraDealSummary || {}), [attr]: { ...entry, label: e.target.value } })} />
                        <TextField size="small" label="Type" value={entry?.type ?? ""} sx={{ minWidth: 100 }} onChange={(e) => onExtraDealSummaryChange({ ...(extraDealSummary || {}), [attr]: { ...entry, type: e.target.value } })} />
                        <IconButton size="small" onClick={() => { const next = { ...(extraDealSummary || {}) }; delete next[attr]; onExtraDealSummaryChange(next); }} aria-label="Remove"><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Box>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={() => { const key = "extraDealSummary" + (Object.keys(extraDealSummary || {}).length + 1); onExtraDealSummaryChange({ ...(extraDealSummary || {}), [key]: { label: "", type: "input" } }); }}>Add entry</Button>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </CardContent>

      <Dialog
        open={openNewCategory}
        onClose={() => {
          setOpenNewCategory(false);
          setNewCategory("");
        }}
      >
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleAddCategory();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenNewCategory(false);
              setNewCategory("");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAddCategory} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FormDetailsSelector;
